import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  ensureSettings,
  useDiscardMedia,
  useSendMessage,
  useTrip,
  useUploadMedia,
} from '../../hooks/queries'
import { merken, neuerSchluessel } from '../../lib/outbox'
import { api, ApiError, netzStand } from '../../api/client'
import type { Media, Settings } from '../../api/types'
import { canPreviewDirectly, isImage, prepareImage } from '../../lib/images'
import { dauerText, grabPoster, isVideo, readVideoInfo, uploadVideo } from '../../lib/video'
import { groesse } from '../../lib/format'
import { GifIcon, MikroIcon, PhotoIcon, SendIcon, StopIcon, VideoIcon, XIcon } from '../../components/Icons'
import { AUSZEICHNUNGEN } from '../../lib/auszeichnung'
import { alsText, auswahlIn, lese, schreibe, zeichneAus } from '../../lib/reichtext'
import {
  HOECHSTENS_MS,
  dauer as dauerText2,
  kannAufnehmen,
  starteAufnahme,
  ueberallAbspielbar,
  type LaufendeAufnahme,
} from '../../lib/sprachaufnahme'
import { Gifwahl, useGifsAktiv } from './Gifwahl'
import type { Bezug } from './ThreadPage'

interface Props {
  tripId: number
  /**
   * Der Hinweis im leeren Feld — **von lang nach kurz**.
   *
   * Auf einem schmalen Telefon passt „Nachricht an alle Empfänger" nicht in
   * die Zeile und wurde abgeschnitten. Statt eines Auslassungszeichens
   * mitten im Wort steht hier eine Staffel: der Composer nimmt die längste
   * Fassung, die wirklich hineinpasst. Der erste Eintrag ist zugleich das,
   * was Vorlesehilfen bekommen — dort zählt die Breite nicht.
   */
  platzhalter: string[]
  disabled?: boolean
  /**
   * Der geöffnete Antwortverlauf. Gesetzt heißt: was hier geschrieben wird,
   * geht an genau diesen Menschen — der Server macht daraus eine private
   * Nachricht.
   */
  withUser?: number
  /**
   * Was statt der Eingabezeile dasteht, wenn nicht geschrieben werden kann.
   *
   * Ohne Angabe: die abgeschlossene Reise, der ursprüngliche Fall. Der Grund
   * gehört an die Stelle, an der sonst das Schreibfeld wäre — ein Feld, das
   * nichts kann, verspricht etwas, das es nicht hält.
   */
  disabledHint?: React.ReactNode
  /**
   * Worauf sich die Nachricht bezieht — gesetzt an einer Sprechblase weiter
   * oben oder an einem Foto in der Großansicht.
   */
  bezug?: Bezug | null
  /** Den Bezug wieder loswerden: das ✕ am Zitat, und nach dem Senden. */
  onBezugWeg?: () => void
}

/**
 * `offline` ist die neue Stufe: das Bild ist fertig gerechnet, aber es gibt
 * kein Netz. Es liegt dann als Blob bereit und geht mit der Nachricht in den
 * Ausgangskorb, statt als Fehler dazustehen.
 */
type Stufe = 'wartet' | 'rechnet' | 'lädt' | 'fertig' | 'offline' | 'fehler'

/**
 * Ein ausgewähltes Foto auf seinem Weg zum Server.
 *
 * Der Schlüssel ist ein eigener: die id vom Server gibt es erst am Ende des
 * Weges, die Kachel aber schon am Anfang.
 */
interface Attachment {
  key: number
  preview: string | null
  stufe: Stufe
  /** Anteil des Hochladens, 0 bis 1. */
  fortschritt: number
  /**
   * Was die Datei wiegt -- und zwar von Anfang an.
   *
   * Der iOS-Teilen-Dialog merkt sich die Größenauswahl nicht (Format und
   * Standort schon). Von derselben Aufnahme kommt deshalb mal 25 MB und mal
   * 300 MB, je nachdem ob man daran gedacht hat. Wer das erst erfährt, wenn
   * der Server ablehnt, hat schon gewartet.
   */
  bytes: number
  art: 'bild' | 'video' | 'ton'
  /** Nur bei Videos, sobald gelesen. */
  dauerMs?: number
  /** Die Grenze, an der diese Datei scheitern wird -- in MB, sonst 0. */
  ueberGrenze?: number
  /**
   * Das fertig verkleinerte Bild.
   *
   * Bleibt liegen, bis die Nachricht raus ist: ohne Netz wandert es in den
   * Ausgangskorb, und dort steht das Bild selbst, nicht seine id vom Server.
   */
  blob?: Blob
  id?: number
  problem?: string
  /**
   * Ein Hinweis, der kein Fehler ist.
   *
   * Bisher gibt es genau einen: das Video konnte nicht verkleinert werden und
   * geht so hinaus, wie es aus der Kamera kam. Das gehört gesagt -- sonst
   * wundert sich jemand, warum ein Upload so lange dauert -- aber es ist
   * nichts fehlgeschlagen, und deshalb ist es nicht `problem`.
   */
  hinweis?: string
  /**
   * Was aus dem Video wurde: „184 MB → 27 MB · 1080p".
   *
   * Steht an der Kachel, weil dort die Frage entsteht — „ist das jetzt
   * kleiner, und wie klein?". Nach dem Absenden ist die Kachel weg; wer es
   * später nachsehen will, findet Größe und Maße am Video im Verlauf.
   */
  ausbeute?: string
}

/**
 * Die kurze Kante — das ist die Zahl, die „1080p" meint.
 *
 * Eine Hochkantaufnahme misst 1080 x 1920; „1920p" stünde dort und wäre
 * falsch. Gemeint ist immer die kleinere der beiden Kanten.
 */
function kurzeKante(masse: { breite: number; hoehe: number }): number {
  if (masse.breite <= 0 || masse.hoehe <= 0) return 0
  return Math.min(masse.breite, masse.hoehe)
}

let naechsterSchluessel = 1

/**
 * Das Vorschaubild zum Bezug -- aus den Daten des offenen Chats.
 *
 * Hier ausnahmsweise aus dem Zwischenspeicher und nicht vom Server: das Zitat
 * über der Eingabezeile sieht **nur der, der gerade schreibt**, und zwar zu
 * einer Nachricht, die vor ihm im selben Verlauf steht. In der Sprechblase
 * einer gesendeten Antwort ist es umgekehrt -- dort liefert der Server den
 * Auszug, denn dort lesen andere mit.
 */
function bezugBild(bezug: Bezug): string | null {
  const bild = bezug.mediaId === undefined
    ? undefined
    : bezug.message.media.find((m) => m.id === bezug.mediaId)
  return bild?.poster_url ?? bild?.url ?? null
}

/** Die Eingabezeile: wächst mit dem Text, Enter sendet, Umschalt+Enter bricht um. */
export function Composer({
  tripId,
  platzhalter,
  disabled,
  withUser,
  disabledHint,
  bezug,
  onBezugWeg,
}: Props) {
  const [text, setText] = useState('')
  const [fotos, setFotos] = useState<Attachment[]>([])
  const [problem, setProblem] = useState<string | null>(null)
  const [sendeWennFertig, setSendeWennFertig] = useState(false)
  const [gifOffen, setGifOffen] = useState(false)
  // Die laufende Aufnahme -- als Ref, weil sie kein Neuzeichnen auslöst, und
  // die verstrichene Zeit daneben als Zustand, weil sie eine.
  const aufnahme = useRef<LaufendeAufnahme | null>(null)
  const [nimmtAuf, setNimmtAuf] = useState(false)
  const [seit, setSeit] = useState(0)
  const [tonHinweis, setTonHinweis] = useState<string | null>(null)
  const field = useRef<HTMLDivElement>(null)
  const picker = useRef<HTMLInputElement>(null)

  // Die Uploads gehen nacheinander, aufgereiht an dieser Kette. Das nächste
  // Bild wird schon verkleinert, während das vorige über die Leitung geht --
  // Rechnen und Senden überlappen, und beides ist auf einem Telefon der
  // Flaschenhals.
  const kette = useRef<Promise<void>>(Promise.resolve())

  const send = useSendMessage(tripId, withUser)
  const upload = useUploadMedia(tripId)
  const discard = useDiscardMedia()
  // Nur für die Uhr der Reise -- die Abfrage liegt im Zwischenspeicher.
  const reiseVersatz = useTrip(tripId).data?.tz_offset_min ?? null
  const client = useQueryClient()
  /**
   * **Der Reisende erzählt, die anderen antworten.**
   *
   * Fotos und Videos sind der Bericht: sie kosten Kontingent und stehen
   * dauerhaft in der Reise. Eine Antwort trägt Worte — und ein GIF, weil das
   * zum Ton gehört. Entschieden wird das am Server (die Rolle, nicht der
   * Client); hier steht nur, was gar nicht erst zur Auswahl kommt.
   *
   * **Für Mitlesende gibt es deshalb keine Dateiauswahl mehr.** Sie war der
   * Umweg: der iOS-Dialog bot dort „Fotos" und „Foto aufnehmen" an, obwohl
   * beides abgewiesen wurde, und wer wirklich ein GIF wollte, musste es sich
   * vorher irgendwo besorgen. GIFs kommen jetzt aus der Auswahl daneben.
   */
  const darfAlles = useTrip(tripId).data?.role === 'sender'
  // Ohne GIPHY_API_KEY steht der Knopf nicht da -- dann bleibt es bei Worten.
  const gifsAktiv = useGifsAktiv()

  /**
   * Ob diese Datei über der Grenze liegt -- und über welcher.
   *
   * Die Grenze gilt ohnehin auf dem Server; hier steht sie nur, damit man es
   * vor dem Warten sieht statt danach. Ohne bekannte Werte wird nichts
   * behauptet.
   */
  function ueberGrenze(
    bytes: number,
    art: 'bild' | 'video' | 'ton',
    werte: Settings | null,
  ): number | undefined {
    if (!werte) return undefined
    const grenze = art === 'video' ? werte.video_max_mb : werte.image_max_mb
    return bytes > grenze * 1024 * 1024 ? grenze : undefined
  }

  // „Beschäftigt" heißt: da läuft noch etwas, warte damit. Ein Bild, das auf
  // Netz wartet, läuft gerade **nicht** -- es wäre sonst für immer beschäftigt
  // und der Beitrag ginge nie in den Korb.
  const beschaeftigt = fotos.some((foto) => unterwegs(foto.stufe))
  // Abgeleitet und nicht gemerkt, und das ist die Korrektur eines Fehlers:
  // vorher stand hier ein eigener Zustand, der beim Auswählen eines Videos auf
  // `true` ging und **nirgends** zurück. Der Hinweis blieb damit stehen, auch
  // nachdem die Nachricht längst raus war -- bis jemand den Chat verließ und
  // neu betrat. Was aus den Anhängen ablesbar ist, gehört nicht daneben noch
  // einmal aufgeschrieben.
  const hatVideo = fotos.some((foto) => foto.art === 'video')
  const fertige = fotos.filter((foto) => foto.stufe === 'fertig')

  /**
   * Ein `contenteditable` wächst von selbst — gedeckelt wird in der CSS
   * (`max-height`). Geblieben ist die Funktion für den Ausgangszustand: nach
   * dem Leeren soll die Zeile wieder einzeilig dastehen.
   */
  function grow() {
    field.current?.scrollTo({ top: field.current.scrollHeight })
  }

  function aendere(key: number, teil: Partial<Attachment>) {
    setFotos((alt) => alt.map((foto) => (foto.key === key ? { ...foto, ...teil } : foto)))
  }

  /**
   * Fotos annehmen -- und zwar sofort.
   *
   * Für jede Datei steht die Kachel da, bevor irgendetwas gerechnet ist. Das
   * ist der ganze Punkt: der Auswahldialog gibt die Bilder ab und man ist
   * zurück im Chat, statt vor einem stehengebliebenen Bildschirm zu warten,
   * bis fünf iPhone-Fotos verkleinert sind.
   */
  async function add(files: FileList | null) {
    if (!files || files.length === 0) return
    setProblem(null)

    const aufgaben = Array.from(files).map((file) => ({
      file,
      key: naechsterSchluessel++,
    }))

    setFotos((alt) => [
      ...alt,
      ...aufgaben.map(({ file, key }) => ({
        key,
        preview: canPreviewDirectly(file) ? URL.createObjectURL(file) : null,
        stufe: 'wartet' as Stufe,
        fortschritt: 0,
        bytes: file.size,
        art: (isVideo(file) ? 'video' : 'bild') as 'bild' | 'video',
      })),
    ])

    // Erst die Werte, dann rechnen. Ein Foto, das mit der Vorgabe statt der
    // eingestellten Größe verkleinert wurde, fällt niemandem auf -- es sieht
    // ja aus wie ein Foto. Deshalb wird hier gewartet, und zwar nur beim
    // ersten Mal: danach steht die Antwort im Zwischenspeicher.
    const werte = await ensureSettings(client)

    // Ein Video geht unverändert raus; ob es durchgeht, steht damit schon
    // jetzt fest. Bei einem Foto entscheidet erst das verkleinerte Bild.
    for (const { file, key } of aufgaben) {
      if (isVideo(file)) {
        aendere(key, { ueberGrenze: ueberGrenze(file.size, 'video', werte) })
      }
    }

    // Hier kommt nur noch an, was der Reisende ausgewaehlt hat: die
    // Dateiauswahl steht nur ihm offen. Die Riegel gegen fremde Fotos und
    // Videos stehen weiterhin im Server -- dort, wo sie hingehoeren.
    for (const { file, key } of aufgaben) {
      // Ein Video geht einen eigenen Weg: **erst verkleinert** (#4), dann in
      // Stücken hochgeladen. Das Standbild entsteht dabei.
      //
      // Hier stand, ein Video werde nicht verkleinert, „der Browser könnte
      // es, aber es dauerte Minuten und macht das Telefon heiß". Das galt für
      // den Weg über ein `<video>`-Element, das dafür abspielen muss. Über
      // den Demuxer läuft es in Lesegeschwindigkeit.
      if (isVideo(file)) {
        // Standbild und Dauer noch vor dem ersten Byte: die Kachel zeigt
        // damit, *was* da gleich rausgeht -- ein Bild statt eines grauen
        // Kastens, und die Länge daneben. Das Standbild wird weitergereicht,
        // damit es nicht zweimal gerechnet wird.
        const vorschau = grabPoster(file)
          .then((blob) => {
            if (blob !== null) aendere(key, { preview: URL.createObjectURL(blob) })
            return blob
          })
          .catch(() => null)
        void readVideoInfo(file)
          .then((info) => aendere(key, { dauerMs: info.durationMs }))
          .catch(() => undefined)

        kette.current = kette.current.then(async () => {
          try {
            aendere(key, { stufe: 'rechnet', fortschritt: 0 })
            const medium = await uploadVideo(
              tripId,
              file,
              (anteil) => aendere(key, { stufe: 'lädt', fortschritt: anteil }),
              {
                // Die Zusage, nicht das Bild: `await` an dieser Stelle hielte
                // den Upload auf, bis das Standbild da ist -- und wenn es nie
                // kommt, für immer.
                poster: vorschau,
                ziel: werte
                  ? { maxEdge: werte.video_max_edge, maxFps: werte.video_max_fps }
                  : undefined,
                onRechnet: (anteil) => aendere(key, { stufe: 'rechnet', fortschritt: anteil }),
                // Keine stille Absage: wenn das Gerät nicht umrechnen kann,
                // geht die Datei so hinaus, wie sie ist -- und das steht dann
                // an der Kachel.
                onGrund: (grund) => aendere(key, { hinweis: grund }),
                // **Die Kachel zeigt danach die wahre Zahl.** Sie stand dort
                // aus der ausgewählten Datei -- nach dem Verkleinern war das
                // die Größe von etwas, das gar nicht hochgeht.
                onVerkleinert: (befund) =>
                  aendere(key, {
                    bytes: befund.nachher,
                    // **Die Grenze gilt für das, was hochgeht.** Ohne diese
                    // Zeile bliebe „über 200 MB" an einer Datei stehen, die
                    // gerade auf 27 MB geschrumpft ist -- und der Upload
                    // gelänge trotzdem. Gerechnet wird mit derselben
                    // Funktion wie beim Auswählen, nicht mit einer zweiten.
                    ueberGrenze: ueberGrenze(befund.nachher, 'video', werte),
                    ausbeute:
                      `${groesse(befund.vorher)} → ${groesse(befund.nachher)}`
                      + (kurzeKante(befund) > 0 ? ` · ${kurzeKante(befund)}p` : ''),
                  }),
              },
            )
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: medium.id })
          } catch (fehler) {
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : 'Das Video ging nicht durch.',
            })
          }
        })
        continue
      }

      if (!isImage(file)) {
        aendere(key, { stufe: 'fehler', problem: 'Das ist weder ein Bild noch ein Video.' })
        continue
      }

      try {
        aendere(key, { stufe: 'rechnet' })
        const prepared = await prepareImage(file, {
          ...(werte ? { maxEdge: werte.image_max_edge, quality: werte.image_quality } : {}),
          // **Auch hier die Uhr der Reise**, obwohl es „nur" ein Chatfoto ist:
          // die Aufnahmezeit entscheidet später mit, welchem Aufenthalt das
          // Bild zugeordnet wird. Eine Stunde daneben, und es fällt aus dem
          // Fenster.
          reiseVersatz,
        })
        // Ab hier zählt das verkleinerte Bild, nicht die Datei aus der
        // Auswahl: das ist die Zahl, die über die Leitung geht.
        aendere(key, {
          bytes: prepared.blob.size,
          ueberGrenze: ueberGrenze(prepared.blob.size, 'bild', werte),
          // Das Bild bleibt hier liegen -- ohne Netz ist es das, was in den
          // Ausgangskorb wandert.
          blob: prepared.blob,
        })

        // Ein Bild ohne eigene Vorschau (HEIC) bekommt sie jetzt.
        setFotos((alt) =>
          alt.map((foto) =>
            foto.key === key && foto.preview === null
              ? { ...foto, preview: URL.createObjectURL(prepared.blob) }
              : foto,
          ),
        )

        kette.current = kette.current.then(async () => {
          // Ohne Netz gar nicht erst anfangen: das Bild ist fertig und wartet
          // mit der Nachricht auf später. Gefragt wird `netzStand()` und nicht
          // `navigator.onLine` -- Letzteres sagt „online", sobald irgendein
          // WLAN da ist, auch wenn nichts hindurchgeht.
          if (!netzStand()) {
            aendere(key, { stufe: 'offline' })
            return
          }

          try {
            aendere(key, { stufe: 'lädt', fortschritt: 0 })
            const media = await upload.mutateAsync({
              blob: prepared.blob,
              // Aus dem Original gelesen, bevor verkleinert wurde -- der
              // Server legt Ort und Zeit an der Datei ab.
              aufnahme: prepared.aufnahme,
              onProgress: (anteil) => aendere(key, { fortschritt: anteil }),
            })
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: media.id })
          } catch (fehler) {
            // Am Netz gescheitert heißt: später. Alles andere ist ein Fehler,
            // den man sehen muss -- ein Bild über der Grenze etwa geht auch
            // beim zehnten Versuch nicht durch.
            if (fehler instanceof ApiError && fehler.offline) {
              aendere(key, { stufe: 'offline' })
              return
            }
            // Ein gescheitertes Bild hält die anderen nicht auf.
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : 'Das Bild ging nicht durch.',
            })
          }
        })
      } catch (fehler) {
        aendere(key, {
          stufe: 'fehler',
          problem: fehler instanceof Error ? fehler.message : 'Das Bild ließ sich nicht öffnen.',
        })
      }
    }
  }

  /**
   * Was gerade angeheftet ist — für das Aufräumen beim Verlassen.
   *
   * Der Effekt darunter läuft genau einmal und sähe sonst für immer die leere
   * Liste vom ersten Zeichnen.
   */
  const angeheftet = useRef<Attachment[]>([])
  angeheftet.current = fotos

  /**
   * Den Chat verlassen, ohne zu senden, räumt die Anhänge weg.
   *
   * Ein Foto geht sofort hinauf, die Nachricht erst beim Senden — wer den Chat
   * dazwischen verlässt, ließ bisher eine Zeile ohne Nachricht und ohne
   * Highlight zurück. Sie fällt niemandem auf, bis sie in „Fotos aus dieser
   * Reise" wieder auftaucht.
   *
   * Nach dem Senden steht hier nichts mehr: `aufraeumen()` leert die Liste,
   * und die ids gehören dann der Nachricht.
   *
   * Gelöscht wird hier **unmittelbar** und nicht über die Mutation: die hängt
   * an einem Bauteil, das gerade verschwindet. Scheitert es (kein Netz), bleibt
   * es beim Kehrbesen nach einem Tag — abreisen darf man trotzdem.
   */
  useEffect(() => {
    return () => {
      for (const foto of angeheftet.current) {
        if (foto.preview) URL.revokeObjectURL(foto.preview)
        if (foto.id !== undefined) void api.delete<void>(`/api/media/${foto.id}`).catch(() => {})
      }
    }
  }, [])

  /**
   * Ein GIF, das der Server schon geholt hat.
   *
   * Es hat bereits eine id und liegt bereits in der Reise -- es ist damit an
   * derselben Stelle wie ein fertig hochgeladenes Bild und geht denselben Weg
   * weiter. Ohne `blob`: in den Ausgangskorb gehört es nicht, denn geholt
   * wurde es ohnehin nur, weil gerade Netz da war.
   */
  function nimmGif(media: Media) {
    setProblem(null)
    setFotos((alt) => [
      ...alt,
      {
        key: naechsterSchluessel++,
        preview: media.url,
        stufe: 'fertig' as Stufe,
        fortschritt: 1,
        bytes: media.bytes,
        art: 'bild' as const,
        id: media.id,
      },
    ])
  }

  function remove(item: Attachment) {
    setFotos((alt) => alt.filter((foto) => foto.key !== item.key))
    if (item.preview) URL.revokeObjectURL(item.preview)
    // Auf dem Server auch wegräumen, sonst zählt es gegen das Kontingent,
    // bis es von selbst verfällt.
    if (item.id !== undefined) discard.mutate(item.id)
  }

  /** Nach dem Absenden: Feld leer, Kacheln weg, Vorschauen freigegeben. */
  function aufraeumen() {
    setText('')
    // Das Feld ist ungesteuert -- geleert wird es hier, ausdrücklich.
    if (field.current !== null) schreibe(field.current, '')
    fotos.forEach((foto) => foto.preview && URL.revokeObjectURL(foto.preview))
    setFotos([])
    setProblem(null)
    // Der Bezug gilt für diese eine Nachricht. Bliebe er stehen, hinge die
    // nächste ungefragt am selben Beitrag.
    onBezugWeg?.()
    requestAnimationFrame(grow)
  }

  /**
   * In den Ausgangskorb legen, statt zu verlieren.
   *
   * Mitgegeben werden die **Bilder**, nicht ihre ids -- auch die schon
   * hochgeladenen. Ein Foto, das vor dem Netzabbruch durchkam, hat zwar eine
   * id, aber die räumt der Server nach einem Tag weg; ein Eintrag, der morgen
   * früh darauf zeigt, wäre kaputt. Lieber einmal doppelt hochladen.
   */
  function inDenKorb(body: string, clientKey: string) {
    void merken({
      tripId,
      withUser,
      body,
      clientKey,
      // Der Bezug muss den Korb überstehen: ohne ihn käme die Antwort später
      // ohne ihren Anfang heraus, und niemand wüsste mehr, worauf sie ging.
      replyToId: bezug?.message.id,
      replyToMediaId: bezug?.mediaId,
      bilder: fotos.map((foto) => foto.blob).filter((blob): blob is Blob => blob !== undefined),
    }).then(aufraeumen)
  }

  /**
   * Eine Auszeichnung auf die Auswahl legen — oder wieder wegnehmen.
   *
   * **Die Auswahl bleibt stehen.** Deshalb hängt der Knopf an `mousedown`
   * mit `preventDefault` (siehe unten): ein Knopf, der den Fokus nimmt,
   * löscht die Markierung, und danach gibt es nichts mehr auszuzeichnen.
   */
  function lege(art: (typeof AUSZEICHNUNGEN)[number]['art']) {
    const feld = field.current
    if (feld === null) return
    zeichneAus(art)
    setText(lese(feld))
    grow()
  }

  /**
   * **Die Leiste steht nur da, solange etwas markiert ist.**
   *
   * Gewünscht war sie „nicht als feste Symbole, sondern als Unterpunkt im
   * Pop-Up-Menü". In Apples eigenes Menü kommt eine Webseite nicht hinein —
   * das kann nur eine native App. Das Nächstliegende ist dies: sie erscheint
   * beim Markieren und verschwindet wieder. Angeschlagen wird sie über
   * `selectionchange`, denn ein `onSelect` gibt es an einem
   * `contenteditable` nicht.
   */
  /**
   * Welche Fassung des Hinweises passt.
   *
   * **Gemessen und nicht geraten:** die Breite eines Textes hängt an der
   * Schrift, und die hängt seit der Schriftgrößen-Einstellung am Gerät.
   * `measureText` fragt genau die Schrift, die im Feld steht — eine
   * Faustzahl („unter 400 Punkten die kurze") ginge bei der nächsten Stufe
   * daneben.
   */
  const [stufe, setStufe] = useState(0)
  useEffect(() => {
    const feld = field.current
    if (feld === null) return

    const messen = () => {
      const stil = getComputedStyle(feld)
      const platz = feld.clientWidth
        - Number.parseFloat(stil.paddingLeft)
        - Number.parseFloat(stil.paddingRight)
      if (platz <= 0) return
      const stift = document.createElement('canvas').getContext('2d')
      if (stift === null) return
      stift.font = `${stil.fontStyle} ${stil.fontWeight} ${stil.fontSize} ${stil.fontFamily}`
      const passt = platzhalter.findIndex((wort) => stift.measureText(wort).width <= platz)
      setStufe(passt === -1 ? platzhalter.length - 1 : passt)
    }

    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(feld)
    return () => beobachter.disconnect()
  }, [platzhalter])

  const [hatAuswahl, setHatAuswahl] = useState(false)
  useEffect(() => {
    const schauen = () => setHatAuswahl(auswahlIn(field.current))
    document.addEventListener('selectionchange', schauen)
    return () => document.removeEventListener('selectionchange', schauen)
  }, [])

  /**
   * Aufnehmen anfangen — und den Zähler mitlaufen lassen.
   *
   * **Keine stille Absage:** verweigert das Gerät das Mikrofon, steht der
   * Grund als Text da. Ein Knopf, auf den nichts folgt, ist das Schlimmste.
   */
  async function nimmAuf() {
    setTonHinweis(null)
    try {
      aufnahme.current = await starteAufnahme()
      setNimmtAuf(true)
      setSeit(0)
    } catch {
      setTonHinweis(
        'Das Mikrofon ist nicht freigegeben. In den Einstellungen des Browsers erlauben.',
      )
    }
  }

  /** Aufnahme beenden und als Anhang aufnehmen — wie ein fertiges Bild. */
  async function beendeAufnahme() {
    const laufend = aufnahme.current
    if (!laufend) return
    aufnahme.current = null
    setNimmtAuf(false)

    const roh = await laufend.stoppen()
    if (roh.blob.size === 0) return

    // **Die Kachel steht da, bevor gerechnet wird** -- dieselbe Reihenfolge
    // wie beim Verkleinern eines Fotos. Wo MP4 herauskam, ist `rechnet` nur
    // ein Wimpernschlag; wo umgewandelt wird, ist es die Auskunft, dass
    // etwas läuft und die Aufnahme nicht verloren ist.
    const key = naechsterSchluessel++
    setFotos((alt) => [
      ...alt,
      {
        key,
        preview: null,
        stufe: 'rechnet' as Stufe,
        fortschritt: 0,
        bytes: roh.blob.size,
        art: 'ton' as const,
        dauerMs: roh.dauerMs,
      },
    ])

    const fertig = await ueberallAbspielbar(roh)
    if (!fertig.ueberall) {
      // Nur noch die letzte Rückfalllinie: das Umwandeln selbst ist
      // gescheitert. Hochgeladen wird trotzdem.
      setTonHinweis(
        'Dein Browser nimmt in einem Format auf, das Safari nicht abspielt, ' +
          'und das Umwandeln hat nicht geklappt. Auf einem iPhone bleibt ' +
          'diese Aufnahme stumm.',
      )
    }
    aendere(key, { stufe: 'lädt', bytes: fertig.blob.size })

    try {
      const medium = await api.upload<Media>(
        `/api/trips/${tripId}/media/voice`,
        fertig.blob,
        (anteil) => aendere(key, { fortschritt: anteil }),
        { 'x-dauer-ms': String(Math.round(fertig.dauerMs)) },
      )
      aendere(key, { stufe: 'fertig', fortschritt: 1, id: medium.id })
    } catch (fehler) {
      aendere(key, {
        stufe: 'fehler',
        problem: fehler instanceof Error ? fehler.message : 'Die Aufnahme ging nicht durch.',
      })
    }
  }

  function brichAufnahmeAb() {
    aufnahme.current?.abbrechen()
    aufnahme.current = null
    setNimmtAuf(false)
  }

  // Der Zähler, und die Decke: bei zwei Minuten ist Schluss, von selbst.
  useEffect(() => {
    if (!nimmtAuf) return
    const uhr = setInterval(() => {
      setSeit((alt) => {
        const neu = alt + 250
        if (neu >= HOECHSTENS_MS) void beendeAufnahme()
        return neu
      })
    }, 250)
    return () => clearInterval(uhr)
    // `beendeAufnahme` hängt an nichts, was sich währenddessen ändert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nimmtAuf])

  function submit() {
    const body = text.trim()
    if (!body && fotos.length === 0) return
    if (send.isPending) return

    // Noch nicht fertig: nicht abweisen, sondern vormerken. Wer unterwegs
    // Fotos schickt, soll das Telefon weglegen können.
    if (beschaeftigt) {
      setSendeWennFertig(true)
      return
    }

    setSendeWennFertig(false)

    // Ein Schlüssel für diese eine Nachricht, gleich hier vergeben: geht der
    // Versuch am Netz kaputt, wandert **derselbe** in den Korb. Sonst stünde
    // der Beitrag doppelt da, wenn der Server ihn längst hatte und nur die
    // Antwort verlorenging.
    const clientKey = neuerSchluessel()

    // Ohne Netz -- oder mit einem Bild, das deshalb liegengeblieben ist --
    // wird gar nicht erst versucht.
    if (!netzStand() || fotos.some((foto) => foto.stufe === 'offline')) {
      // Ein Video wartet nicht im Korb: zweihundert Megabyte gehören nicht in
      // IndexedDB, und stillschweigend weglassen wäre schlimmer als absagen.
      if (fotos.some((foto) => foto.art === 'video')) {
        setProblem(
          'Ein Video braucht Netz. Nimm es heraus — der Text und die Fotos gehen dann später von selbst raus.',
        )
        return
      }
      inDenKorb(body, clientKey)
      return
    }

    if (!body && fertige.length === 0) return

    send.mutate(
      {
        body,
        mediaIds: fertige.map((foto) => foto.id!),
        clientKey,
        replyToId: bezug?.message.id,
        replyToMediaId: bezug?.mediaId,
      },
      {
        onSuccess: aufraeumen,
        onError: (fehler) => {
          // Am Netz gescheitert: nicht verloren, sondern verschoben. Eine
          // Absage des Servers dagegen bleibt eine Absage -- sie stünde im
          // Korb bis in alle Ewigkeit.
          if (fehler instanceof ApiError && fehler.offline) inDenKorb(body, clientKey)
        },
      },
    )
  }

  // Der vorgemerkte Versand, sobald das letzte Bild durch ist. Ein
  // gescheitertes Bild nimmt die Vormerkung zurück -- sonst ginge der Beitrag
  // ohne das Foto raus, das man eigentlich zeigen wollte.
  useEffect(() => {
    if (!sendeWennFertig || beschaeftigt) return
    if (fotos.some((foto) => foto.stufe === 'fehler')) {
      setSendeWennFertig(false)
      setProblem('Ein Foto kam nicht durch. Schau es dir an und schick es dann ab.')
      return
    }
    setSendeWennFertig(false)
    submit()
    // submit hängt an Text und Fotos; die stehen zu diesem Zeitpunkt fest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendeWennFertig, beschaeftigt])

  if (disabled) {
    return <p className="composer__hint">{disabledHint ?? 'Diese Reise ist abgeschlossen.'}</p>
  }

  const wartet = fotos.filter((foto) => unterwegs(foto.stufe)).length

  return (
    <div className="composer-area">
      {/* Worauf das hier antwortet -- direkt über dem Feld, in dem man
          schreibt. Weiter oben, bei der Sprechblase selbst, wäre es beim
          Tippen längst aus dem Bild. */}
      {bezug && (
        <div className="bezug">
          {bezugBild(bezug) && <img className="bezug__bild" src={bezugBild(bezug)!} alt="" />}
          <span className="bezug__text">
            <span className="bezug__autor">
              Antwort an {bezug.message.author.display_name}
              {bezug.mediaId !== undefined && ' · Foto'}
            </span>
            <span className="bezug__auszug">
              {bezug.message.body !== '' ? bezug.message.body : 'Foto'}
            </span>
          </span>
          <button
            type="button"
            className="icon-button bezug__weg"
            title="Bezug entfernen"
            aria-label="Bezug entfernen"
            onClick={() => onBezugWeg?.()}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      {(fotos.length > 0 || problem) && (
        <div className="attachments">
          {fotos.map((foto) => (
            <Kachel key={foto.key} foto={foto} onRemove={() => remove(foto)} />
          ))}

          {problem && <span className="attachments__problem">{problem}</span>}
        </div>
      )}

      {/* **Der Grund gehört hin, wo man ihn liest.** Er stand nur als
          `title` an der Kachel -- ein Tooltip, den auf einem Telefon nie
          jemand sieht. Aus der Prüfung dieser Runde: ein abgewiesenes Foto
          sah dort aus wie ein Fehlschlag ohne Erklärung. */}
      {fotos.some((f) => f.stufe === 'fehler' && f.problem) && (
        <p className="composer__status composer__status--fehler" role="alert">
          {[...new Set(fotos.filter((f) => f.stufe === 'fehler').map((f) => f.problem))]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}

      {/* **Was da rechnet, gehoert benannt.** Ein Balken ohne Wort sagt „warte",
          aber nicht worauf -- und Verkleinern ist das einzige, was hier lange
          dauern kann. */}
      {fotos.some((f) => f.stufe === 'rechnet' && f.art === 'video') && (
        <p className="composer__status" role="status">
          Video wird verkleinert …
        </p>
      )}

      {fotos.some((f) => f.hinweis) && (
        <p className="composer__status" role="status">
          {[...new Set(fotos.map((f) => f.hinweis))].filter(Boolean).join(' · ')}
        </p>
      )}

      {(wartet > 0 || sendeWennFertig) && (
        <p className="composer__status" role="status">
          {sendeWennFertig
            ? 'Wird gesendet, sobald alles durch ist …'
            : `${fertige.length} von ${fotos.length} fertig …`}
        </p>
      )}

      {/* Ein Knopfdruck, auf den nichts folgt, ist das Schlimmste, was eine
          Eingabezeile tun kann -- man drückt noch einmal und noch einmal. Der
          Server schickt in jedem dieser Fälle einen brauchbaren Satz; er muss
          nur ankommen. */}
      {send.isError && !(send.error instanceof ApiError && send.error.offline) && (
        <p className="composer__status composer__status--fehler" role="alert">
          {send.error instanceof Error ? send.error.message : 'Die Nachricht ging nicht raus.'}
        </p>
      )}

      {/* Der einzige Hebel gegen ein Video, das nur auf Apple-Geräten läuft,
          liegt beim Absender -- und zwar bevor er es auswählt. Deshalb steht
          der Satz hier und nicht in einer Fußnote. */}
      {hatVideo && (
        <p className="composer__status">
          Videos gehen unverändert raus. Wähle beim Teilen auf dem iPhone
          <strong> „Am kompatibelsten"</strong>, damit sie auch am Rechner und
          auf Android laufen.
        </p>
      )}

      {gifOffen && (
        <Gifwahl tripId={tripId} onGewaehlt={nimmGif} onZu={() => setGifOffen(false)} />
      )}

      {/* **Die Reihe aus vier Knöpfen ist weg.**

          Seit die Eingabezeile ein `contenteditable` ist, bietet iOS von
          selbst *Format ▸ Fett · Kursiv · Unterstreichen* im eigenen
          Auswahlmenü an — dafür braucht es hier nichts mehr. Aus dem
          Betrieb: „die Knopfsymbole wegnehmen, die benötigen viel
          Anzeigeplatz."

          **Durchgestrichen und Kapitälchen kennt Apples Menü nicht**, und
          eine Webseite kann dort nichts hinzufügen — das kann nur eine native
          App. Die beiden stehen deshalb in der Eingabezeile selbst (unten),
          und zwar nur, solange etwas markiert ist: keine eigene Reihe, kein
          Platz im Ruhezustand. */}

      {/* Was das Mikrofon zu sagen hat, steht als Text da — nicht als
          Tooltip, den auf dem Telefon niemand sieht. */}
      {tonHinweis && (
        <p className="composer__status composer__status--fehler" role="alert">
          {tonHinweis}
        </p>
      )}

      <div className="composer">
        {/* Die Dateiauswahl gehört dem Reisenden. Für alle anderen stand hier
            ein Dialog, der Fotos anbot und Fotos ablehnte. */}
        {darfAlles && (
          <>
            <input
              ref={picker}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={(event) => {
                void add(event.target.files)
                // Zurücksetzen, sonst löst dieselbe Datei kein zweites Mal aus.
                event.target.value = ''
              }}
            />
            <button
              type="button"
              className="composer__attach"
              onClick={() => picker.current?.click()}
              aria-label="Fotos und Videos hinzufügen"
              title="Fotos und Videos hinzufügen"
            >
              <PhotoIcon size={20} />
            </button>
          </>
        )}

        {gifsAktiv && (
          <button
            type="button"
            className={gifOffen ? 'composer__attach composer__attach--an' : 'composer__attach'}
            onClick={() => setGifOffen((offen) => !offen)}
            aria-label="GIF auswählen"
            aria-expanded={gifOffen}
            title="GIF auswählen"
          >
            <GifIcon size={20} />
          </button>
        )}

        {/* **Was Apples Menü nicht kann — und nur, solange markiert ist.**
            Fett und Kursiv holt man sich dort; diese beiden gibt es nicht,
            also stehen sie hier. Sie erscheinen mit der Markierung und
            verschwinden mit ihr, kosten also im Ruhezustand keinen Platz. */}
        {hatAuswahl &&
          AUSZEICHNUNGEN.filter((a) => a.art === 'durch' || a.art === 'kapitael').map((a) => (
            <button
              key={a.zeichen}
              type="button"
              className={`composer__attach auszeichnen__knopf--${a.art}`}
              title={a.name}
              aria-label={a.name}
              // Ein Knopf nimmt beim Antippen den Fokus, und mit dem Fokus
              // geht die Markierung -- danach wäre nichts mehr auszuzeichnen.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => lege(a.art)}
            >
              {a.art === 'durch' ? 'D' : 'Kap'}
            </button>
          ))}

        {/* **Ein `contenteditable`, kein `textarea`.** Ein Textfeld kann eine
            Schrift haben, nicht zwei — fett sähe darin aus wie mager, und die
            Sternchen stünden weiter da. Warum der Trick mit einer Fassung
            dahinter nicht geht, steht in `reichtext.ts`.

            **Ungesteuert, mit Absicht.** Der Zustand wird aus dem Feld
            gelesen, nie hineingeschrieben; ein `value`, das React bei jedem
            Tastendruck zurückschreibt, setzte den Cursor ans Ende. Geschrieben
            wird nur, wenn die Anwendung es meint: nach dem Senden. */}
        <div
          ref={field}
          className="composer__input"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          data-leer={platzhalter[stufe] ?? platzhalter[0]}
          aria-label={platzhalter[0]}
          onInput={() => {
            setText(lese(field.current as HTMLElement))
            grow()
          }}
          onPaste={(event) => {
            // Aus einer fremden Seite kommt fremdes HTML. Hier zählt der
            // Wortlaut, und sonst nichts.
            event.preventDefault()
            alsText(event.clipboardData.getData('text/plain'))
            setText(lese(field.current as HTMLElement))
            grow()
          }}
          onKeyDown={(event) => {
            // Auf einem Telefon gibt es keine Umschalt-Taste im Blickfeld;
            // dort bleibt die Eingabetaste ein Zeilenumbruch und gesendet wird
            // über den Knopf.
            if (event.key === 'Enter' && !event.shiftKey && !isTouch()) {
              event.preventDefault()
              submit()
            }
          }}
        />
        {/* **Aufnehmen darf jeder** — der Reisende und die Mitlesenden. Eine
            gesprochene Antwort ist eine Antwort, kein Beitrag zum Bericht;
            wie weit sie reicht, entscheidet danach dieselbe Regel wie bei
            jeder Nachricht. Der Knopf steht nur da, wo der Browser wirklich
            aufnehmen kann. */}
        {kannAufnehmen() && !text.trim() && (
          nimmtAuf ? (
            <>
              <span className="composer__aufnahme" role="status">
                <span className="composer__punkt" aria-hidden="true" />
                {dauerText2(seit)}
              </span>
              <button
                type="button"
                className="icon-button composer__weg"
                onClick={brichAufnahmeAb}
                aria-label="Aufnahme verwerfen"
                title="Verwerfen"
              >
                <XIcon size={18} />
              </button>
              <button
                type="button"
                className="composer__send composer__send--stop"
                onClick={() => void beendeAufnahme()}
                aria-label="Aufnahme beenden"
                title="Fertig"
              >
                <StopIcon size={18} />
              </button>
            </>
          ) : (
            <button
              type="button"
              className="composer__attach"
              onClick={() => void nimmAuf()}
              aria-label="Sprachnachricht aufnehmen"
              title="Sprachnachricht aufnehmen"
            >
              <MikroIcon size={20} />
            </button>
          )
        )}

        {!nimmtAuf && (
          <button
            type="button"
            className="composer__send"
            disabled={(!text.trim() && fotos.length === 0) || send.isPending || sendeWennFertig}
            onClick={submit}
            aria-label="Senden"
            title="Senden"
          >
            <SendIcon size={20} />
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Eine Kachel je Foto -- mit dem Stand der Dinge darauf.
 *
 * Vorbereiten und Hochladen sind zwei verschiedene Wartezeiten: das eine
 * hängt am Telefon, das andere am Netz. Wer unterwegs steht und sich fragt,
 * ob es am Empfang liegt, sieht es hier.
 */
/** Ob an dieser Kachel gerade gearbeitet wird -- Warten auf Netz zählt nicht. */
function unterwegs(stufe: Stufe): boolean {
  return stufe === 'wartet' || stufe === 'rechnet' || stufe === 'lädt'
}

function Kachel({ foto, onRemove }: { foto: Attachment; onRemove: () => void }) {
  const laeuft = unterwegs(foto.stufe)

  return (
    <div className="attachment-item">
    <div
      className={foto.stufe === 'fehler' ? 'attachment attachment--fehler' : 'attachment'}
      title={foto.problem ?? undefined}
    >
      {foto.preview ? (
        <img src={foto.preview} alt="" className={laeuft ? 'attachment__blass' : undefined} />
      ) : (
        <span className="attachment__leer">
          {/* Ohne Standbild soll wenigstens dastehen, um was es sich
              handelt -- ein Bildsymbol an einem Video führt in die Irre. */}
          {foto.art === 'video' ? (
            <VideoIcon size={18} />
          ) : foto.art === 'ton' ? (
            <MikroIcon size={18} />
          ) : (
            <PhotoIcon size={18} />
          )}
        </span>
      )}

      {laeuft && (
        <span className="attachment__stufe">
          {/* **Auch beim Rechnen eine Zahl.** Hier stand `…` fuer alles ausser
              dem Hochladen -- bei einem Foto ist das ein Wimpernschlag, beim
              Verkleinern eines Videos steht man eine Minute davor und weiss
              nicht, ob noch etwas passiert. Die Punkte bleiben fuer den
              Moment, in dem es noch keine Zahl gibt. */}
          {foto.fortschritt > 0 && foto.stufe !== 'wartet'
            ? `${Math.round(foto.fortschritt * 100)} %`
            : '…'}
        </span>
      )}

      {/* Fertig gerechnet, aber ohne Netz: die Kachel sagt, dass sie wartet --
          sonst sähe sie aus wie eine, die durch ist. */}
      {foto.stufe === 'offline' && (
        <span className="attachment__stufe" title="Wartet auf Netz">
          ⏳
        </span>
      )}

      {foto.stufe === 'lädt' && (
        <span className="attachment__balken">
          <span style={{ width: `${Math.round(foto.fortschritt * 100)}%` }} />
        </span>
      )}

      {foto.stufe === 'fehler' && <span className="attachment__stufe">!</span>}

      {/* Solange es läuft, gibt es nichts wegzunehmen -- der Upload wäre
          ohnehin schon unterwegs. */}
      {!laeuft && (
        <button
          type="button"
          className="attachment__remove"
          onClick={onRemove}
          aria-label="Foto wieder entfernen"
          title="Entfernen"
        >
          <XIcon size={13} />
        </button>
      )}
    </div>

    {/* Was da rausgeht, in Zahlen. Bei einem verkleinerten Foto ist es eine
        Beruhigung, bei einem Video aus dem Teilen-Dialog manchmal eine
        Warnung -- und beides sieht man besser vorher als hinterher. */}
    <span
      className={
        foto.ueberGrenze ? 'attachment__groesse attachment__groesse--zuviel' : 'attachment__groesse'
      }
      title={foto.ueberGrenze ? `Die Grenze liegt bei ${foto.ueberGrenze} MB.` : undefined}
    >
      {groesse(foto.bytes)}
      {(foto.art === 'video' || foto.art === 'ton') &&
      foto.dauerMs !== undefined &&
      foto.dauerMs > 0
        ? ` · ${dauerText(foto.dauerMs)}`
        : ''}
      {/* Nicht erst am Ende des Uploads: die Zahl steht ohnehin da, und
          daneben passt der Grund, warum sie zu hoch ist. */}
      {foto.ueberGrenze ? ` · über ${foto.ueberGrenze} MB` : ''}
      {/* Was das Verkleinern gebracht hat -- die Zahl daneben beantwortet die
          Frage, die man sich beim Zusehen stellt. */}
      {foto.ausbeute ? ` · ${foto.ausbeute}` : ''}
    </span>
    </div>
  )
}

function isTouch(): boolean {
  return window.matchMedia('(pointer: coarse)').matches
}
